/**
 * Reports Page
 *
 * Displays Power BI reports in an iframe using secure proxy URLs.
 * Uses the selected report from AppContext.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useReports } from '../hooks/useReports';
import { api } from '../services/api';

export const ReportsPage = () => {
  const { reports, currentReport, isLoading, error, reload } = useReports();

  // Embed state
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);

  /**
   * Fetch embed token for the current report
   */
  const fetchEmbedToken = useCallback(async (reportId: string) => {
    setEmbedLoading(true);
    setEmbedError(null);
    setEmbedUrl(null);

    // Clear any existing refresh timeout
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    try {
      const response = await api.reports.getEmbed(reportId);

      if (response.error) {
        setEmbedError(response.error);
        return;
      }

      if (response.data) {
        setEmbedUrl(response.data.proxyUrl);

        // Set up auto-refresh before token expires (refresh 30 seconds before expiry)
        const refreshIn = (response.data.expiresIn - 30) * 1000;
        if (refreshIn > 0) {
          refreshTimeoutRef.current = window.setTimeout(() => {
            fetchEmbedToken(reportId);
          }, refreshIn);
        }
      }
    } catch (err) {
      setEmbedError(err instanceof Error ? err.message : 'Failed to get embed token');
    } finally {
      setEmbedLoading(false);
    }
  }, []);

  // Fetch embed token when report changes
  useEffect(() => {
    if (currentReport) {
      fetchEmbedToken(currentReport.id);
    } else {
      setEmbedUrl(null);
      setEmbedError(null);
    }

    // Cleanup on unmount or report change
    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [currentReport, fetchEmbedToken]);

  // Loading reports state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-metrica-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  // Error loading reports
  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load reports</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={reload}
            className="px-4 py-2 bg-metrica-green text-white rounded-lg hover:bg-metrica-green/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No reports
  if (reports.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Reports Available</h2>
          <p className="text-gray-600">
            There are no reports configured for your account. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // No report selected
  if (!currentReport) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Select a Report</h2>
          <p className="text-lg text-gray-500">Choose a report from the sidebar to view</p>
        </div>
      </div>
    );
  }

  // Loading embed token
  if (embedLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-metrica-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  // Error loading embed token
  if (embedError) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load report</h2>
          <p className="text-gray-600 mb-4">{embedError}</p>
          <button
            onClick={() => currentReport && fetchEmbedToken(currentReport.id)}
            className="px-4 py-2 bg-metrica-green text-white rounded-lg hover:bg-metrica-green/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Waiting for embed URL
  if (!embedUrl) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Preparing report...</p>
        </div>
      </div>
    );
  }

  // Display report in iframe (full height, title shown in TopBar)
  return (
    <div className="h-full bg-gray-50">
      <iframe
        src={embedUrl}
        title={currentReport.name}
        className="w-full h-full border-0"
        allowFullScreen
      />
    </div>
  );
};

export default ReportsPage;
